import {
  PropsWithChildren,
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react'
import { useLocation } from 'react-router-dom'

import { INITIAL_STATE } from 'components/NavigationV3/NavigationV3Provider.constants'
import { NavigationV3ContextType } from 'components/NavigationV3/NavigationV3Provider.types'
import { useNavigationV3Queries } from 'components/NavigationV3/hooks/useNavigationV3Queries'
import { useNavigationV3Functions } from 'components/NavigationV3/hooks/useNavigationV3Functions'
import { useFetchStytchDataForNavigationV3 } from 'components/NavigationV3/hooks/useFetchStytchDataForNavigationV3'
import { getStorageItem, setStorageItem } from 'lib/utils/storageUtils'
import { useIsMobile } from 'hooks/useIsMobile'

export const NavigationV3Context =
  createContext<NavigationV3ContextType>(INITIAL_STATE)

// Session storage key for navigation collapsed state
const NAVIGATION_COLLAPSED_KEY = 'sequence_navigation_collapsed'

export const NavigationV3Provider = ({ children }: PropsWithChildren) => {
  const [isSwitchingWorkspace, setIsSwitchingWorkspace] =
    useState<boolean>(false)

  // Mobile-only slide-over drawer state. Always starts closed.
  const [isMobileNavOpen, setIsMobileNavOpen] = useState<boolean>(false)
  const isMobile = useIsMobile()
  const location = useLocation()

  // Initialize isNavigationCollapsed from session storage or default to false (expanded)
  const [isNavigationCollapsed, setIsNavigationCollapsed] = useState<boolean>(
    () => {
      // In test environments, always start expanded
      if (process.env.NODE_ENV === 'test') {
        return false
      }

      return getStorageItem(NAVIGATION_COLLAPSED_KEY, false, sessionStorage)
    }
  )

  const { userEmail, userWorkspaces, workspaceId, workspaceName } =
    useFetchStytchDataForNavigationV3()
  const { queries } = useNavigationV3Queries({ userWorkspaces })

  // Toggle navigation collapsed state and persist to session storage
  const toggleNavigationCollapsed = () => {
    setIsNavigationCollapsed(prevState => {
      const newState = !prevState
      setStorageItem(NAVIGATION_COLLAPSED_KEY, newState, sessionStorage)
      return newState
    })
  }

  const toggleMobileNav = useCallback(
    () => setIsMobileNavOpen(prev => !prev),
    []
  )
  const closeMobileNav = useCallback(() => setIsMobileNavOpen(false), [])

  // Dismiss the drawer on every navigation, so it never lingers over the new
  // content. Keyed on `location.key`: a query-string-only link still navigates.
  useEffect(() => {
    closeMobileNav()
  }, [location.key, closeMobileNav])

  const { functions: baseFunctions } = useNavigationV3Functions({
    setIsSwitchingWorkspace,
    toggleCollapsed: toggleNavigationCollapsed
  })

  const functions = useMemo(
    () => ({ ...baseFunctions, toggleMobileNav, closeMobileNav }),
    [baseFunctions, toggleMobileNav, closeMobileNav]
  )

  const data = useMemo(() => {
    return {
      isSwitchingWorkspace,
      workspaceId: workspaceId || '',
      workspaceName: workspaceName || '',
      userEmail: userEmail || '',
      userWorkspaces: userWorkspaces || [],
      isNavigationCollapsed,
      isEffectivelyCollapsed: isNavigationCollapsed && !isMobile,
      isMobileNavOpen
    }
  }, [
    isSwitchingWorkspace,
    userEmail,
    userWorkspaces,
    workspaceId,
    workspaceName,
    isNavigationCollapsed,
    isMobile,
    isMobileNavOpen
  ])

  return (
    <NavigationV3Context.Provider value={{ data, queries, functions }}>
      {children}
    </NavigationV3Context.Provider>
  )
}
